/* eslint-disable react-hooks/exhaustive-deps */
import DisplayAllNotifications from "@/components/info/display-all-notifications";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTransmitChannel } from "@/hooks/use-transmit-channel";
import { requestNotificationSound } from "@/lib/audio-notification-coordinator";
import {
  normalizeNotificationSoundSettings,
  resolveNotificationSound,
} from "@/lib/notification-sounds";
import { Notification, SharedData } from "@/types";
import { router, usePage } from "@inertiajs/react";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/es";
import relativeTime from "dayjs/plugin/relativeTime";
import { Bell } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import { Skeleton } from "../ui/skeleton";
import NotificationCard from "./notification-card";
import { urlFor } from "~/client";

// Initialize dayjs with relativeTime plugin
dayjs.extend(relativeTime);

// Notifications must always render newest-first. The backend already orders by
// createdAt desc, but realtime payloads arrive over time, so sort defensively
// instead of trusting array insertion order.
function sortByCreatedDesc<T extends { createdAt?: string | null }>(items: T[]): T[] {
  return [...items].toSorted(
    (a, b) => dayjs(b.createdAt ?? 0).valueOf() - dayjs(a.createdAt ?? 0).valueOf(),
  );
}

function DisplayNotifications() {
  const { t, i18n } = useTranslation("notifications");
  const [isOpen, setIsOpen] = useState(false);
  // Set dayjs locale based on i18n language
  const currentLocale = i18n.language;
  dayjs.locale(currentLocale || "en");
  const { user } = usePage<SharedData>().props.auth;
  const rawSoundSettings = usePage<SharedData>().props.auth.notificationSoundSettings;
  const soundSettings = useMemo(
    () => normalizeNotificationSoundSettings(rawSoundSettings),
    [rawSoundSettings],
  );
  const { notifications: sharedNotifications } = usePage<SharedData>().props;
  const [prevIds, setPrevIds] = useState<string[]>([]);
  // NotificationCard reads `message`/`url` at the top level, so every
  // notification entering state must keep them there regardless of source
  // (shared props on page load vs realtime broadcast payloads).
  type BellNotification = Notification & {
    message?: string;
    url?: string | null;
    invitationToken?: string | null;
  };
  type NotificationState = {
    unreadCount: number;
    allNotifications: BellNotification[];
    unreadNotifications: BellNotification[];
  };

  const [notifications, setNotifications] = useState<NotificationState>({
    unreadCount: 0,
    allNotifications: [],
    unreadNotifications: [],
  });

  type LegacyNotification = {
    id: string;
    type: string;
    message: string;
    url: string | null;
    invitationToken?: string | null;
    data?: Notification["data"];
    created_at: string;
    read_at: string | null;
    updated_at: string;
    notifiable_id: string;
    notifiable_type: string;
  };

  const normalizeNotification = (
    notification: Notification | LegacyNotification,
  ): BellNotification => {
    if ("created_at" in notification) {
      return {
        id: notification.id,
        type: notification.type,
        data: notification.data ?? { message: notification.message, url: notification.url },
        message: notification.message,
        url: notification.url,
        invitationToken: notification.invitationToken ?? null,
        readAt: notification.read_at,
        createdAt: notification.created_at,
        updatedAt: notification.updated_at,
        notifiableId: notification.notifiable_id,
        notifiableType: notification.notifiable_type,
      } as BellNotification;
    }

    const data = notification.data as Record<string, unknown> | undefined;
    const message = typeof data?.message === "string" ? data.message : undefined;
    const body = typeof data?.body === "string" ? data.body : undefined;
    const url = typeof data?.url === "string" ? data.url : null;
    // Realtime broadcast payloads skip the transformer, so derive the
    // invitation token the same way toSharedData does.
    const invitationToken =
      data?.type === "company_invitation" && typeof data.token === "string" ? data.token : null;
    return { ...notification, message: message ?? body, url, invitationToken };
  };

  // Initialize notifications from shared data if available
  useEffect(() => {
    if (
      sharedNotifications &&
      "unreadCount" in sharedNotifications &&
      "allNotifications" in sharedNotifications &&
      "unreadNotifications" in sharedNotifications
    ) {
      setNotifications({
        unreadCount: sharedNotifications.unreadCount,
        allNotifications: sortByCreatedDesc(
          sharedNotifications.allNotifications.map(normalizeNotification),
        ),
        unreadNotifications: sortByCreatedDesc(
          sharedNotifications.unreadNotifications.map(normalizeNotification),
        ),
      });
    }
  }, [sharedNotifications]);

  // When new notifications are received, play a sound if it's a conversation assignment
  useEffect(() => {
    if (!notifications.unreadNotifications || !Array.isArray(notifications.unreadNotifications)) {
      return;
    }

    const currentIds = notifications.unreadNotifications.map((n: Notification) => n.id);
    if (prevIds.length && currentIds.length > prevIds.length) {
      // Find the new notification(s)
      const newIds = new Set(currentIds.filter((id) => !prevIds.includes(id)));
      const newNotifs = notifications.unreadNotifications.filter((n: Notification) =>
        newIds.has(n.id),
      );
      if (!soundSettings.enabled) {
        return;
      }

      const notification = newNotifs[0];
      const data = notification?.data as Record<string, unknown> | undefined;
      const companyIntegrationId =
        typeof data?.company_integration_id === "number" ||
        typeof data?.company_integration_id === "string"
          ? data.company_integration_id
          : null;
      const sound = resolveNotificationSound(soundSettings, companyIntegrationId);
      requestNotificationSound({
        id: `notification:${String(notification.id)}`,
        sound,
        volume: soundSettings.volume,
      });
    }
    setPrevIds(currentIds);
  }, [notifications.unreadNotifications, soundSettings]);

  useTransmitChannel<LegacyNotification>(
    user?.id ? `${window.location.origin}/user/${user.id}/notifications` : null,
    (data) => {
      if (!data || !data.id) {
        return;
      }
      const notification = normalizeNotification(data);
      setNotifications((prev) => {
        // Ignore duplicates (a broadcast can race with a props refresh).
        if ((prev.unreadNotifications || []).some((n) => n.id === notification.id)) {
          return prev;
        }
        return {
          ...prev,
          unreadCount: (prev.unreadCount ?? 0) + 1,
          // Prepend + sort so the newest notification renders first, and keep it
          // in allNotifications so the "view all" history stays complete.
          unreadNotifications: sortByCreatedDesc([
            notification,
            ...(prev.unreadNotifications || []),
          ]),
          allNotifications: sortByCreatedDesc([notification, ...(prev.allNotifications || [])]),
        };
      });
    },
  );

  const handleMarkAllAsRead = () => {
    // Optimistically clear the bell so it updates instantly.
    setNotifications((prev) => ({
      ...prev,
      unreadCount: 0,
      unreadNotifications: [],
    }));
    router.visit(urlFor("notifications.markAllAsRead"), {
      onSuccess: () => {
        // The notifications prop is lazy on Inertia requests, so explicitly
        // refresh it to keep the "view all" list in sync too.
        router.reload({ only: ["notifications"] });
        toast.success(t("markAsRead.success"));
      },
      onError: () => {
        toast.error(t("markAsRead.error"));
      },
    });
  };

  const getUnreadCountMessage = () => {
    return t("count.unread", {
      count: notifications.unreadCount,
    });
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" title={getUnreadCountMessage()}>
          <Bell className="size-4" />
          {notifications.unreadCount > 0 && (
            <Badge variant="default" className="absolute -top-1 -right-1 text-xs">
              {notifications.unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      {isOpen ? (
        <PopoverContent className="w-lvw md:w-[450px] lg:w-[600px]">
          <div className="flex items-center justify-between font-medium">
            {t("title")}
            <Button
              variant="default"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={notifications.unreadCount === 0}
              className="text-xs"
            >
              {t("header.markAllAsRead")}
            </Button>
          </div>
          <div className="bg-border my-2 h-px" />
          {!notifications.allNotifications ? (
            <div className="p-2">
              <Skeleton className="h-4 w-[200px]" />
              <Skeleton className="mt-2 h-4 w-[160px]" />
            </div>
          ) : notifications.allNotifications?.length > 0 ? (
            // if there are unread notifications, show them
            notifications.unreadNotifications &&
            Array.isArray(notifications.unreadNotifications) &&
            notifications.unreadNotifications.length > 0 ? (
              <ScrollArea className="h-[250px]">
                <div className="space-y-1">
                  {notifications.unreadNotifications.map((notification: Notification) => (
                    <div key={notification.id}>
                      <NotificationCard notification={notification} />
                      <Separator />
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              // if there are no unread notifications,show message all notifications are read
              <div className="text-muted-foreground p-2">{t("dropdown.allRead")}</div>
            )
          ) : (
            <div className="text-muted-foreground p-2">{t("empty.state")}</div>
          )}
          <div className="bg-border my-2 h-px" />
          <Button variant="ghost" className="notification-item w-full">
            <DisplayAllNotifications
              className="w-full"
              notifications={notifications.allNotifications}
            />
          </Button>
        </PopoverContent>
      ) : null}
    </Popover>
  );
}

export default DisplayNotifications;
